/* eslint-disable max-lines */
import { useEffect, useState } from 'react';

import { Entry } from 'contentful';
import { NextPageWithLayout } from 'next';
import { useRouter } from 'next/navigation';

import { Alert } from '../../components/Alert/Alert';
import { BragBarCarousel } from '../../components/BragBar/BragBarCarousel';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { QuoteSection } from '../../components/QuoteSection/QuoteSection';
import { Redirect } from '../../components/Redirect/Redirect';
import { ReviewSectionList } from '../../components/ReviewSection/ReviewSectionList';
import { StickyBannerGetPro } from '../../components/StickyBottomBanner/StickyBannerGetPro';
import { PRIMARY_OFFER_ID, type ProPlanId } from '../../constants/premium';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../helpers/analytics/trackEvent';
import { reportMykissError } from '../../helpers/errorHandling';
import { useCoupon } from '../../helpers/hooks/useCoupon';
import { useCurrentURL } from '../../helpers/hooks/useCurrentURL/useCurrentURL';
import { useDistanceFromTop } from '../../helpers/hooks/useDistanceFromTop';
import { useIsMobileDevice } from '../../helpers/hooks/useIsMobileDevice';
import { useScreenHeight } from '../../helpers/hooks/useScreenHeight';
import { useIsTabletScreenWidthOrWider } from '../../helpers/hooks/useScreenWidth';
import { useUserInfo } from '../../helpers/hooks/useUserInfo';
import { TypeProPageSkeleton } from '../../interfaces/contentful';
import {
  URLParameters,
  companiesURLParameters,
  flowsURLParameters,
} from '../Onboarding/constants/url';

import { CMSChoosePaymentPlanSection } from './components/CMSChoosePaymentPlanSection/CMSChoosePaymentPlanSection';
import { CMSFeatureInDetailSection } from './components/CMSFeatureInDetailSection/CMSFeatureInDetailSection';
import { CMSFinePrintSection } from './components/CMSFinePrintSection';
import { CMSPartnerHeader } from './components/CMSPartnerHeader/CMSPartnerHeader';
import { CMSProFeaturesSection } from './components/CMSProFeaturesSection';

export interface IProPageProps {
  proPageData: Entry<TypeProPageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>;
  couponCode?: string;
  canonicalPath: string;
}

export const CMSProPage: NextPageWithLayout<IProPageProps> = ({
  proPageData,
  couponCode,
  canonicalPath,
}: IProPageProps) => {
  const { isPremium, premiumEndDate } = useUserInfo();
  const isTabletScreenWidthOrWider = useIsTabletScreenWidthOrWider();
  const isMobileDevice = useIsMobileDevice();
  const [proPlanId, setProPlanId] = useState(PRIMARY_OFFER_ID);
  const { coupon, isLoading: couponIsLoading, codeIsInvalid } = useCoupon(couponCode);
  const [userHasBeenPro, setUserHasBeenPro] = useState(false);

  const [showInvalidCodeError, setShowInvalidCodeError] = useState<boolean>(false);
  const { currentPage } = useAnalytics();
  const distanceFromTop = useDistanceFromTop();
  const screenHeight = useScreenHeight();

  const {
    slug: campaignSlug,
    pageTitle,
    pageDescription,
    partnerHeader,
    choosePaymentPlanOne,
    choosePaymentPlanTwo,
    choosePaymentPlanThree,
    choosePaymentPlanFour,
    quoteOne,
    quoteTwo,
    quoteThree,
    bragBarOne,
    bragBarTwo,
    proFeaturesListOne,
    proFeaturesListTwo,
    proFeaturesListThree,
    reviewSectionOne,
    reviewSectionTwo,
    featureInDetailOne,
    featureInDetailTwo,
    featureInDetailThree,
    cmsProPFinePrint,
  } = proPageData.fields;

  const { push } = useRouter();
  const { addParameters, hasParameters } = useCurrentURL();
  const submitLink = addParameters({
    [URLParameters.Company]: companiesURLParameters.fishbrain,
    [URLParameters.Flow]: flowsURLParameters.fishbrain.becomePro,
  });
  const hasOnboardingUrlParams = hasParameters([URLParameters.Company, URLParameters.Flow]);

  const handleSubmitLinkClick = (): void => {
    sessionStorage.setItem('subscription.plan.id', JSON.stringify(proPlanId));
  };

  const handleAlertClose = (): void => {
    setShowInvalidCodeError(false);
    try {
      push('/pro');
    } catch (error) {
      reportMykissError(error);
    }
  };

  useEffect(() => {
    const currentDate = new Date();
    if (premiumEndDate) {
      const result = premiumEndDate.toString().localeCompare(currentDate.toString());
      setUserHasBeenPro(result <= 0);
    }
  }, [premiumEndDate]);

  useEffect(() => {
    const handleSessionStorageChange = (): void => {
      const planIdFromStorage = sessionStorage.getItem('subscription.plan.id');
      setProPlanId(
        (planIdFromStorage && (JSON.parse(planIdFromStorage) as ProPlanId)) || PRIMARY_OFFER_ID,
      );
    };

    window.addEventListener('planInStorageChanged', handleSessionStorageChange);

    handleSessionStorageChange();

    return () => {
      window.removeEventListener('planInStorageChanged', handleSessionStorageChange);
    };
  }, []);

  useEffect(() => {
    if (coupon) {
      setProPlanId(coupon.group.stripe_plan.external_id);
    }
  }, [coupon]);

  useEffect(() => {
    if (codeIsInvalid) {
      setShowInvalidCodeError(true);
    }
  }, [codeIsInvalid]);

  if (isPremium && !hasOnboardingUrlParams) {
    return <Redirect to="/profile" />;
  }

  if (couponCode && codeIsInvalid) {
    return <Redirect to={`/pro-page/${campaignSlug}`} />;
  }

  return (
    <div className="bg-lake-fog">
      <MetaTags
        title={pageTitle ?? 'Fish smarter with Fishbrain Pro'}
        ogTitle={couponCode ?? 'Claim your Fishbrain Pro offer'}
        description={
          pageDescription ??
          'Fishbrain pro helps you fish smarter today. Check out features like depth maps, catch logging, fising regulations and much more'
        }
        link={canonicalPath}
      />

      {showInvalidCodeError && (
        <Alert
          bodyCopy="The coupon code is not valid anymore"
          alertShade="pinktail"
          onInteraction={handleAlertClose}
        />
      )}

      {partnerHeader?.fields && (
        <CMSPartnerHeader
          headerTitle={partnerHeader.fields.headerTitle}
          headerText={partnerHeader.fields.headerText}
          buttonText={partnerHeader.fields.buttonText}
          bannerImageDesktop={partnerHeader.fields.bannerImageDesktop?.fields.file}
          bannerImageTablet={partnerHeader.fields.bannerImageTablet?.fields.file}
          bannerImageMobile={partnerHeader.fields.bannerImageMobile?.fields.file}
          logoDesktop={partnerHeader.fields.logoDesktop?.fields.file}
          logoMobile={partnerHeader.fields.logoMobile?.fields.file}
          currentPage={currentPage}
          coupon={coupon}
          couponIsLoading={couponIsLoading}
          userHasBeenPro={userHasBeenPro}
          proPlanId={proPlanId}
          onPlanSelect={setProPlanId}
          submitLink={submitLink}
          onSubmitLinkClick={handleSubmitLinkClick}
        />
      )}

      {!partnerHeader?.fields && choosePaymentPlanOne?.fields && (
        <header>
          <CMSChoosePaymentPlanSection
            userHasBeenPro={userHasBeenPro}
            couponIsLoading={couponIsLoading}
            coupon={coupon}
            mainTitle={choosePaymentPlanOne.fields.mainTitle}
            offerTitle={choosePaymentPlanOne.fields.offerTitle}
            planCardTitle={choosePaymentPlanOne.fields.planCardTitle}
            phonePreviewDesktop={choosePaymentPlanOne.fields.phonePreviewDesktop?.fields.file}
            phonePreviewTablet={choosePaymentPlanOne.fields.phonePreviewTablet?.fields.file}
            phonePreviewMobile={choosePaymentPlanOne.fields.phonePreviewMobile?.fields.file}
            currentPage={currentPage}
            position={1}
            proPlanId={proPlanId}
            onPlanSelect={setProPlanId}
            submitLink={submitLink}
            onSubmitLinkClick={handleSubmitLinkClick}
          />
        </header>
      )}

      {proFeaturesListOne?.fields && (
        <CMSProFeaturesSection proFeaturesEntry={proFeaturesListOne} />
      )}

      {quoteOne?.fields && (
        <QuoteSection
          quote={quoteOne.fields.quote}
          author={quoteOne.fields.author}
          aboutAuthor={quoteOne.fields.aboutAuthor}
          authorImage={quoteOne.fields.authorImage?.fields.file}
        />
      )}

      {bragBarOne?.fields && <BragBarCarousel bragBar={bragBarOne} />}

      {reviewSectionOne?.fields && (
        <ReviewSectionList cmsReviews={reviewSectionOne} lightBackground={false} />
      )}

      {choosePaymentPlanTwo?.fields && isTabletScreenWidthOrWider && (
        <CMSChoosePaymentPlanSection
          userHasBeenPro={userHasBeenPro}
          couponIsLoading={couponIsLoading}
          coupon={coupon}
          mainTitle={choosePaymentPlanTwo.fields.mainTitle}
          offerTitle={choosePaymentPlanTwo.fields.offerTitle}
          planCardTitle={choosePaymentPlanTwo.fields.planCardTitle}
          phonePreviewDesktop={choosePaymentPlanTwo.fields.phonePreviewDesktop?.fields.file}
          phonePreviewTablet={choosePaymentPlanTwo.fields.phonePreviewTablet?.fields.file}
          phonePreviewMobile={choosePaymentPlanTwo.fields.phonePreviewMobile?.fields.file}
          currentPage={currentPage}
          position={2}
          proPlanId={proPlanId}
          onPlanSelect={setProPlanId}
          submitLink={submitLink}
          onSubmitLinkClick={handleSubmitLinkClick}
        />
      )}

      {quoteTwo?.fields && (
        <QuoteSection
          quote={quoteTwo.fields.quote}
          author={quoteTwo.fields.author}
          aboutAuthor={quoteTwo.fields.aboutAuthor}
          authorImage={quoteTwo.fields.authorImage?.fields.file}
        />
      )}

      {proFeaturesListTwo?.fields && (
        <CMSProFeaturesSection proFeaturesEntry={proFeaturesListTwo} />
      )}

      {choosePaymentPlanThree?.fields && isTabletScreenWidthOrWider && (
        <CMSChoosePaymentPlanSection
          userHasBeenPro={userHasBeenPro}
          couponIsLoading={couponIsLoading}
          coupon={coupon}
          mainTitle={choosePaymentPlanThree.fields.mainTitle}
          offerTitle={choosePaymentPlanThree.fields.offerTitle}
          planCardTitle={choosePaymentPlanThree.fields.planCardTitle}
          phonePreviewDesktop={choosePaymentPlanThree.fields.phonePreviewDesktop?.fields.file}
          phonePreviewTablet={choosePaymentPlanThree.fields.phonePreviewTablet?.fields.file}
          phonePreviewMobile={choosePaymentPlanThree.fields.phonePreviewMobile?.fields.file}
          currentPage={currentPage}
          position={3}
          proPlanId={proPlanId}
          onPlanSelect={setProPlanId}
          submitLink={submitLink}
          onSubmitLinkClick={handleSubmitLinkClick}
        />
      )}

      {featureInDetailOne?.fields && (
        <CMSFeatureInDetailSection
          title={featureInDetailOne.fields.title}
          appOnlyPill={featureInDetailOne.fields.appOnly}
          featureDescription={featureInDetailOne.fields.featureDescription}
          buttonText={featureInDetailOne.fields.buttonText}
          phoneRight={featureInDetailOne.fields.phoneRight}
          backgroundTitle={
            featureInDetailOne.fields.backgroundDesktop?.fields.title ??
            featureInDetailOne.fields.backgroundTablet?.fields.title ??
            featureInDetailOne.fields.backgroundMobile?.fields.title ??
            'feature in detail background'
          }
          backgroundDesktop={featureInDetailOne.fields.backgroundDesktop?.fields.file}
          backgroundMobile={featureInDetailOne.fields.backgroundMobile?.fields.file}
          backgroundTablet={featureInDetailOne.fields.backgroundTablet?.fields.file}
          phonePreviewDesktop={featureInDetailOne.fields.phonePreviewDesktop?.fields.file}
          phonePreviewTablet={featureInDetailOne.fields.phonePreviewTablet?.fields.file}
          phonePreviewMobile={featureInDetailOne.fields.phonePreviewMobile?.fields.file}
          position={1}
          currentPage={currentPage}
          submitLink={submitLink}
          onSubmitLinkClick={handleSubmitLinkClick}
        />
      )}

      {quoteThree?.fields && (
        <QuoteSection
          quote={quoteThree.fields.quote}
          author={quoteThree.fields.author}
          aboutAuthor={quoteThree.fields.aboutAuthor}
          authorImage={quoteThree.fields.authorImage?.fields.file}
        />
      )}

      {featureInDetailTwo?.fields && (
        <CMSFeatureInDetailSection
          title={featureInDetailTwo.fields.title}
          appOnlyPill={featureInDetailTwo.fields.appOnly}
          featureDescription={featureInDetailTwo.fields.featureDescription}
          buttonText={featureInDetailTwo.fields.buttonText}
          phoneRight={featureInDetailTwo.fields.phoneRight}
          backgroundTitle={
            featureInDetailTwo.fields.backgroundDesktop?.fields.title ??
            featureInDetailTwo.fields.backgroundTablet?.fields.title ??
            featureInDetailTwo.fields.backgroundMobile?.fields.title ??
            'feature in detail background'
          }
          backgroundDesktop={featureInDetailTwo.fields.backgroundDesktop?.fields.file}
          backgroundMobile={featureInDetailTwo.fields.backgroundMobile?.fields.file}
          backgroundTablet={featureInDetailTwo.fields.backgroundTablet?.fields.file}
          phonePreviewDesktop={featureInDetailTwo.fields.phonePreviewDesktop?.fields.file}
          phonePreviewTablet={featureInDetailTwo.fields.phonePreviewTablet?.fields.file}
          phonePreviewMobile={featureInDetailTwo.fields.phonePreviewMobile?.fields.file}
          position={2}
          currentPage={currentPage}
          submitLink={submitLink}
          onSubmitLinkClick={handleSubmitLinkClick}
        />
      )}

      {featureInDetailThree?.fields && (
        <CMSFeatureInDetailSection
          title={featureInDetailThree.fields.title}
          appOnlyPill={featureInDetailThree.fields.appOnly}
          featureDescription={featureInDetailThree.fields.featureDescription}
          buttonText={featureInDetailThree.fields.buttonText}
          phoneRight={featureInDetailThree.fields.phoneRight}
          backgroundTitle={
            featureInDetailThree.fields.backgroundDesktop?.fields.title ??
            featureInDetailThree.fields.backgroundTablet?.fields.title ??
            featureInDetailThree.fields.backgroundMobile?.fields.title ??
            'feature in detail background'
          }
          backgroundDesktop={featureInDetailThree.fields.backgroundDesktop?.fields.file}
          backgroundMobile={featureInDetailThree.fields.backgroundMobile?.fields.file}
          backgroundTablet={featureInDetailThree.fields.backgroundTablet?.fields.file}
          phonePreviewDesktop={featureInDetailThree.fields.phonePreviewDesktop?.fields.file}
          phonePreviewTablet={featureInDetailThree.fields.phonePreviewTablet?.fields.file}
          phonePreviewMobile={featureInDetailThree.fields.phonePreviewMobile?.fields.file}
          position={3}
          currentPage={currentPage}
          submitLink={submitLink}
          onSubmitLinkClick={handleSubmitLinkClick}
        />
      )}

      {proFeaturesListThree?.fields && (
        <CMSProFeaturesSection proFeaturesEntry={proFeaturesListThree} />
      )}

      {reviewSectionTwo?.fields && (
        <ReviewSectionList cmsReviews={reviewSectionTwo} lightBackground={false} />
      )}

      {bragBarTwo?.fields && <BragBarCarousel bragBar={bragBarTwo} />}

      {choosePaymentPlanFour?.fields && isTabletScreenWidthOrWider && (
        <CMSChoosePaymentPlanSection
          userHasBeenPro={userHasBeenPro}
          couponIsLoading={couponIsLoading}
          coupon={coupon}
          mainTitle={choosePaymentPlanFour.fields.mainTitle}
          offerTitle={choosePaymentPlanFour.fields.offerTitle}
          planCardTitle={choosePaymentPlanFour.fields.planCardTitle}
          phonePreviewDesktop={choosePaymentPlanFour.fields.phonePreviewDesktop?.fields.file}
          phonePreviewTablet={choosePaymentPlanFour.fields.phonePreviewTablet?.fields.file}
          phonePreviewMobile={choosePaymentPlanFour.fields.phonePreviewMobile?.fields.file}
          currentPage={currentPage}
          position={4}
          proPlanId={proPlanId}
          onPlanSelect={setProPlanId}
          submitLink={submitLink}
          onSubmitLinkClick={handleSubmitLinkClick}
        />
      )}

      {cmsProPFinePrint && cmsProPFinePrint.length > 0 && cmsProPFinePrint[0] !== undefined && (
        <CMSFinePrintSection cmsProPFinePrint={cmsProPFinePrint} />
      )}

      {isMobileDevice && distanceFromTop > (screenHeight / 3) * 2 && (
        <StickyBannerGetPro
          selectedPlanId={proPlanId}
          coupon={coupon}
          couponIsLoading={couponIsLoading}
          submitLink={submitLink}
          onSubmitLinkClick={() => {
            void trackEvent({
              name: 'mykiss_cta_button_clicked',
              properties: {
                type: 'pro',
                source: 'sticky-bottom-banner',
                page: currentPage,
              },
            });
          }}
        />
      )}
    </div>
  );
};
